import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { inZone } from '../../utils/companyDate';
import { formatCurrency, formatPriceOrPending, priceIsKnown, PRICE_PENDING_LABEL, portalPriceText } from '../../utils/formatCurrency';
import { formatPhone } from '../../utils/formatPhone';
import usePullToRefresh from '../../hooks/usePullToRefresh';

function CustomerServices() {
  const navigate = useNavigate();
  const { customer, logout, getAuthHeader, isAuthenticated, loading: authLoading } = usePortalAuth();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/portal/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const loadServices = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/portal/services`,
        { headers: getAuthHeader() }
      );
      setServices(response.data);
    } catch (err) {
      console.error('Failed to load services:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      loadServices();
    }
  }, [isAuthenticated, loadServices]);

  const { PullToRefreshIndicator } = usePullToRefresh(loadServices);

  const loadPhotos = async (serviceId) => {
    setPhotosLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/portal/services/${serviceId}/photos`,
        { headers: getAuthHeader() }
      );
      setPhotos(response.data);
    } catch (err) {
      console.error('Failed to load photos:', err);
      setPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  };

  const openServiceDetails = (service) => {
    setSelectedService(service);
    if (service.photoCount > 0) {
      loadPhotos(service.id);
    } else {
      setPhotos([]);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  // completedAt is an instant; the day shown is the company's, not the phone's.
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', inZone(customer?.timezone, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }));
  };

  const calculateTotal = (service) => {
    if (service.isComp) return 0;
    // The stored total when the server sends one: it includes the on-call
    // premium and add-ons, which the parts below do not.
    if (service.total !== null && service.total !== undefined) return service.total;
    let total = service.price;
    if (service.saltApplied && service.saltPrice) {
      total += service.saltPrice;
    }
    if (service.extraChargeAmount) {
      total += service.extraChargeAmount;
    }
    if (service.discountAmount && service.discountType) {
      if (service.discountType === 'percent') {
        total -= total * (service.discountAmount / 100);
      } else {
        total -= service.discountAmount;
      }
    }
    return Math.max(0, total);
  };

  if (authLoading) {
    return (
      <div className="portal-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <PullToRefreshIndicator />
      <header className="portal-header">
        <div className="portal-header-content">
          <div className="portal-brand">
            <h1>{customer?.company?.name || 'Customer Portal'}</h1>
          </div>
          <nav className="portal-nav">
            <Link to="/portal" className="portal-nav-link">Dashboard</Link>
            <Link to="/portal/services" className="portal-nav-link active">Services</Link>
            <Link to="/portal/invoices" className="portal-nav-link">Invoices</Link>
            <button onClick={handleLogout} className="portal-nav-link logout">Logout</button>
          </nav>
        </div>
      </header>

      <main className="portal-main">
        <div className="portal-container">
          <div className="page-header">
            <h2>Service History</h2>
            <p>View all services performed at your property</p>
          </div>

          {loading ? (
            <div className="portal-loading-inline">Loading services...</div>
          ) : services.length === 0 ? (
            <div className="portal-empty-state">
              <p>No services recorded yet.</p>
            </div>
          ) : (
            <div className="services-grid">
              {services.map(service => (
                <div
                  key={service.id}
                  className="service-card"
                  onClick={() => openServiceDetails(service)}
                >
                  <div className="service-card-header">
                    <span className="service-date">{formatDate(service.completedAt)}</span>
                    <span className={`payment-badge ${service.paymentStatus}`}>
                      {service.isComp ? 'COMP' : service.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                    </span>
                  </div>
                  <div className="service-card-body">
                    <div className="service-route">{service.baseLabel || 'Service'}</div>
                    <div className="service-info">
                      {service.saltApplied && (
                        <span className="info-tag">{`${service.saltLabelShort || 'Salt'} Applied`}</span>
                      )}
                      {service.photoCount > 0 && <span className="info-tag photo">📷 {service.photoCount}</span>}
                    </div>
                  </div>
                  <div className="service-card-footer">
                    {/* calculateTotal starts from service.price, which is 0 when no base
                        price was set, so a stop with a $25 extra charge and no base would
                        total $25: a number smaller than the truth, printed as the truth.
                        An unknown SALT price does the same thing one level down, with the
                        base set, so priceIsKnown covers both rather than the base alone. */}
                    <span className={`service-total${priceIsKnown(service) ? '' : ' price-pending'}`}>
                      {portalPriceText(service, calculateTotal(service))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Service Detail Modal */}
      {selectedService && (
        <div className="modal-overlay" onClick={() => setSelectedService(null)}>
          <div className="service-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Service Details</h3>
              <button className="modal-close" onClick={() => setSelectedService(null)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Date</span>
                <span className="detail-value">{formatDate(selectedService.completedAt)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Service</span>
                <span className="detail-value">{selectedService.baseLabel || 'Service'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Base Price</span>
                <span className={`detail-value${selectedService.priceKnown === false ? ' price-pending' : ''}`}>
                  {selectedService.pendingDepth === true
                    ? portalPriceText(selectedService, null)
                    : formatPriceOrPending(selectedService.price, selectedService.priceKnown)}
                </span>
              </div>
              {selectedService.onCallPremiumAmount > 0 && (
                <div className="detail-row">
                  <span className="detail-label">On-call premium ({Number(selectedService.onCallPremiumPercent)}%)</span>
                  <span className="detail-value">+{formatCurrency(selectedService.onCallPremiumAmount)}</span>
                </div>
              )}
              {selectedService.saltApplied && (
                <div className="detail-row">
                  <span className="detail-label">{selectedService.saltLabel || 'Salt Application'}</span>
                  {/* No leading + in the pending case: "+Not yet priced" is not English. */}
                  {selectedService.saltPriceKnown === false ? (
                    <span className="detail-value price-pending">{PRICE_PENDING_LABEL}</span>
                  ) : (
                    <span className="detail-value">+{formatCurrency(selectedService.saltPrice)}</span>
                  )}
                </div>
              )}
              {selectedService.extraChargeAmount > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Additional Charge</span>
                  {/* The reason is the owner's own note to themselves and their crews, and the
                      server no longer sends it. The charge and the amount still show,
                      because hiding a charge is worse than an unexplained one. */}
                  <span className="detail-value">
                    +{formatCurrency(selectedService.extraChargeAmount)}
                  </span>
                </div>
              )}
              {/* Turns an unexplained charge into a phone call rather than a surprise.
                  Omitted entirely when the company has no phone number on file. */}
              {selectedService.extraChargeAmount > 0 && customer?.company?.phone && (
                <div className="detail-row">
                  <span className="charge-help">
                    Questions about this charge? Call us at {formatPhone(customer.company.phone)}.
                  </span>
                </div>
              )}
              {selectedService.discountAmount > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Discount</span>
                  <span className="detail-value discount">
                    -{selectedService.discountType === 'percent'
                      ? `${selectedService.discountAmount}%`
                      : `${formatCurrency(selectedService.discountAmount)}`
                    }
                  </span>
                </div>
              )}
              {selectedService.isComp && (
                <div className="detail-row">
                  <span className="detail-label">Comp</span>
                  <span className="detail-value comp">Complimentary, no charge</span>
                </div>
              )}
              <div className="detail-row total">
                <span className="detail-label">Total</span>
                {/* Unknown if EITHER part is unknown. The rows above each state their
                    own part, so a customer sees Base Price $75, Salt "Not yet priced"
                    and a Total that does not pretend the salt was free. */}
                <span className={`detail-value${priceIsKnown(selectedService) ? '' : ' price-pending'}`}>
                  {portalPriceText(selectedService, calculateTotal(selectedService))}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Payment Status</span>
                <span className={`payment-status ${selectedService.paymentStatus}`}>
                  {selectedService.isComp ? 'Comp' : selectedService.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                </span>
              </div>

              {/* Photos Section */}
              {selectedService.photoCount > 0 && (
                <div className="photos-section">
                  <h4>Service Photos</h4>
                  {photosLoading ? (
                    <div className="photos-loading">Loading photos...</div>
                  ) : photos.length > 0 ? (
                    <div className="photos-grid">
                      {photos.map(photo => (
                        <img
                          key={photo.id}
                          src={photo.photo_url}
                          alt="Service"
                          className="photo-thumb"
                          onClick={() => setSelectedPhoto(photo)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="photos-empty">No photos available</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full Photo Modal */}
      {selectedPhoto && (
        <div className="photo-modal-overlay" data-no-ptr onClick={() => setSelectedPhoto(null)}>
          <button className="photo-modal-close" onClick={() => setSelectedPhoto(null)} aria-label="Close photo">×</button>
          <img src={selectedPhoto.photo_url} alt="Service" className="photo-full" />
        </div>
      )}

      <style>{`
        .portal-page {
          min-height: 100vh;
          background: #f1f5f9;
        }
        .portal-loading {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
        }
        .portal-loading .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .portal-header {
          background: linear-gradient(135deg, var(--color-primary, #1e88e5) 0%, var(--color-primary-dark, #1565c0) 100%);
          color: white;
          padding: 16px 20px;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .portal-header-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .portal-brand h1 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
        }
        .portal-nav {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .portal-nav-link {
          background: rgba(255,255,255,0.1);
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 16px;
          border-radius: 6px;
          text-decoration: none;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.15s;
        }
        .portal-nav-link:hover, .portal-nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .portal-nav-link.logout {
          background: rgba(239,68,68,0.2);
          color: #fca5a5;
        }
        .portal-nav-link.logout:hover {
          background: rgba(239,68,68,0.3);
          color: white;
        }
        .portal-main {
          padding: 24px 20px;
        }
        .portal-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        .page-header {
          margin-bottom: 24px;
        }
        .page-header h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 4px 0;
        }
        .page-header p {
          color: #64748b;
          margin: 0;
        }
        .portal-loading-inline {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }
        .portal-empty-state {
          background: white;
          padding: 40px;
          border-radius: 12px;
          text-align: center;
          color: #64748b;
        }
        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }
        .service-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          cursor: pointer;
          transition: all 0.15s;
        }
        .service-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          transform: translateY(-2px);
        }
        .service-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .service-date {
          font-size: 0.875rem;
          color: #64748b;
        }
        .payment-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .payment-badge.paid {
          background: #dcfce7;
          color: #15803d;
        }
        .payment-badge.not_paid, .payment-badge.invoice_sent {
          background: #fef3c7;
          color: #92400e;
        }
        .service-card-body {
          margin-bottom: 12px;
        }
        .service-route {
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 8px;
        }
        .service-info {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .info-tag {
          background: #f1f5f9;
          color: #475569;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
        }
        .info-tag.photo {
          background: rgba(59,130,246,0.1);
          color: #3b82f6;
        }
        .service-card-footer {
          border-top: 1px solid #e2e8f0;
          padding-top: 12px;
        }
        .service-total {
          font-size: 1.25rem;
          font-weight: 700;
          color: #0f172a;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 200;
          padding: 20px;
        }
        .service-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
        }
        .modal-header h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          color: #64748b;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }
        .modal-body {
          padding: 20px;
        }
        .detail-row {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .detail-row.total {
          border-top: 2px solid #e2e8f0;
          border-bottom: none;
          margin-top: 8px;
          padding-top: 16px;
        }
        .detail-label {
          color: #64748b;
          font-size: 0.9rem;
        }
        .detail-value {
          font-weight: 500;
          color: #0f172a;
        }
        .detail-value.discount {
          color: #16a34a;
        }
        .detail-value.comp {
          color: #3b82f6;
        }
        .detail-row.total .detail-value {
          font-size: 1.25rem;
          font-weight: 700;
        }
        /* "Not yet priced" is grey and italic so the eye reads it as a note and
           cannot mistake a short phrase for a figure. It overrides the total's
           1.25rem, because a large italic phrase looks like a headline. */
        .price-pending {
          color: #64748b;
          font-style: italic;
          font-weight: 500;
        }
        .detail-row.total .detail-value.price-pending {
          font-size: 1rem;
          font-weight: 600;
        }
        .service-total.price-pending {
          font-size: 0.95rem;
        }
        .charge-help {
          display: block;
          font-size: 0.8125rem;
          color: #64748b;
          line-height: 1.4;
        }
        .payment-status {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: 600;
        }
        .payment-status.paid {
          background: #dcfce7;
          color: #15803d;
        }
        .payment-status.not_paid, .payment-status.invoice_sent {
          background: #fef3c7;
          color: #92400e;
        }
        .photos-section {
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }
        .photos-section h4 {
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 12px 0;
        }
        .photos-loading, .photos-empty {
          color: #64748b;
          font-size: 0.9rem;
        }
        .photos-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
          gap: 8px;
        }
        .photo-thumb {
          width: 100%;
          aspect-ratio: 1;
          object-fit: cover;
          border-radius: 8px;
          cursor: pointer;
          transition: transform 0.15s;
        }
        .photo-thumb:hover {
          transform: scale(1.05);
        }
        .photo-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 300;
        }
        .photo-modal-close {
          position: absolute;
          top: 20px;
          right: 20px;
          background: transparent;
          border: none;
          color: white;
          font-size: 32px;
          cursor: pointer;
          z-index: 301;
        }
        .photo-full {
          max-width: 90vw;
          max-height: 90vh;
          object-fit: contain;
          border-radius: 8px;
        }

        @media (max-width: 640px) {
          .portal-header-content {
            flex-direction: column;
            align-items: flex-start;
          }
          .services-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default CustomerServices;
